import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Printer, MapPin } from "lucide-react";
import api from "../lib/api";
import { MAS_MAILIN } from "../lib/constants";

export default function PaymentResult({ cancelled }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [attempts, setAttempts] = useState(0);
  const [subId, setSubId] = useState(null);

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let active = true;
    const poll = async () => {
      if (attempts > 8) { if (active) setState("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.submission_id) setSubId(data.submission_id);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } catch { setTimeout(() => setAttempts((a) => a + 1), 2000); }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, attempts, cancelled]);

  const wrap = (icon, title, msg, ctas) => (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-md text-center bg-white border border-slate-200 rounded-3xl p-12 shadow-sm">
        {icon}
        <h1 className="font-display font-extrabold text-3xl tracking-tighter mt-6 text-[#0A0A0A]" data-testid="payment-result-title">{title}</h1>
        <p className="text-slate-600 mt-3">{msg}</p>
        <div className="flex gap-3 justify-center mt-8">{ctas}</div>
      </div>
    </div>
  );

  const portalBtn = <Link to="/portal" className="px-7 py-3.5 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-[#171717] transition-colors duration-300">Go to Portal</Link>;
  const submitBtn = <Link to="/submit" className="px-7 py-3.5 border-2 border-slate-200 rounded-full text-[#0A0A0A] font-bold hover:border-[#0A0A0A] transition-colors duration-300">New Submission</Link>;

  if (state === "checking") return wrap(<Loader2 className="mx-auto text-[#0A0A0A] animate-spin" size={48} />, "Confirming Payment", "Please wait while we confirm your payment with Stripe…", null);

  if (state === "paid") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full text-center bg-white border border-slate-200 rounded-3xl p-10 md:p-12 shadow-sm">
          <CheckCircle2 className="mx-auto text-emerald-500" size={48} />
          <h1 className="font-display font-extrabold text-3xl tracking-tighter mt-6 text-[#0A0A0A]" data-testid="payment-result-title">Payment Successful</h1>
          <p className="text-slate-600 mt-3">Your submission is confirmed. Print the packing slip, place it in your package, and mail your cards to MAS.</p>

          <div className="mt-8 text-left rounded-2xl bg-[#0A0A0A] text-white p-6">
            <div className="flex items-center gap-2 text-white/50 text-[11px] font-bold uppercase tracking-[0.2em]"><MapPin size={14} /> Ship your cards to</div>
            <div className="mt-3 font-display font-bold text-lg leading-snug">
              {MAS_MAILIN.line1}<br />{MAS_MAILIN.line2}<br />{MAS_MAILIN.line3}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            {subId && (
              <Link to={`/packing-slip/${subId}`} data-testid="print-packing-slip-link" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-[#171717] transition-colors duration-300">
                <Printer size={16} /> Print Packing Slip
              </Link>
            )}
            <Link to="/portal" className="px-7 py-3.5 border-2 border-slate-200 rounded-full text-[#0A0A0A] font-bold hover:border-[#0A0A0A] transition-colors duration-300">Go to Portal</Link>
          </div>
          <p className="text-xs text-slate-400 mt-5">A copy of your packing slip has been emailed to you.</p>
        </div>
      </div>
    );
  }

  if (state === "cancelled") return wrap(<XCircle className="mx-auto text-[#D4AF37]" size={48} />, "Payment Cancelled", "Your payment was cancelled. Your submission is saved as pending — you can pay anytime from your portal.", <>{portalBtn}{submitBtn}</>);
  if (state === "failed") return wrap(<XCircle className="mx-auto text-red-500" size={48} />, "Payment Failed", "We couldn't confirm your payment. Please try again.", <>{submitBtn}</>);
  return wrap(<Loader2 className="mx-auto text-[#0A0A0A]" size={48} />, "Still Processing", "This is taking longer than expected. Check your portal shortly.", <>{portalBtn}</>);
}
